import { useState } from 'react'
import { Plus, ChevronDown, ChevronRight, Search, Pencil, Trash2 } from 'lucide-react'
import { Modal, ConfirmModal } from '../common/Modal'

const FAQS = [
  { id:1, q:'How do I reset my password?',              a:'Go to Settings → Security and click "Change Password". You will receive a reset email within 2 minutes.', cat:'Account' },
  { id:2, q:'How do I upgrade my subscription plan?',  a:'Navigate to Billing → Subscription and select your desired plan. Changes take effect immediately.', cat:'Billing' },
  { id:3, q:'Can I export my data?',                   a:'Yes. Go to Reports → Export and choose your format (CSV, PDF, JSON). Enterprise plans support bulk exports.', cat:'Data' },
  { id:4, q:'How many users can I add to my account?', a:'This depends on your plan. Starter: 5 users, Pro: 25 users, Enterprise: unlimited.', cat:'Account' },
  { id:5, q:'Is there an API available?',               a:'Yes, a full REST API is available on Pro and Enterprise plans. See Settings → API Keys to get started.', cat:'Technical' },
  { id:6, q:'What payment methods do you accept?',     a:'We accept all major credit/debit cards, PayPal, and bank transfers for annual plans.', cat:'Billing' },
]

export default function FAQs() {
  const [faqs, setFaqs] = useState(FAQS)
  const [open, setOpen] = useState(null)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState({ q: '', a: '', cat: 'Account' })
  const filtered = faqs.filter(f => f.q.toLowerCase().includes(search.toLowerCase()) || f.cat.toLowerCase().includes(search.toLowerCase()))

  const openAdd = () => {
    setActive(null)
    setForm({ q: '', a: '', cat: 'Account' })
    setModalOpen(true)
  }

  const openEdit = (faq) => {
    setActive(faq)
    setForm({ q: faq.q, a: faq.a, cat: faq.cat })
    setModalOpen(true)
  }

  const save = () => {
    if (!form.q.trim() || !form.a.trim()) return
    if (active) {
      setFaqs((prev) => prev.map((f) => (f.id === active.id ? { ...f, ...form } : f)))
    } else {
      setFaqs((prev) => [{ id: Date.now(), ...form }, ...prev])
    }
    setModalOpen(false)
  }

  const onDelete = () => {
    setFaqs((prev) => prev.filter((f) => f.id !== active.id))
    setDeleteOpen(false)
    setActive(null)
  }

  return (
    <div className="dash-page page-in">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'var(--space-md)'}}>
        <h1 className="page-title">FAQs</h1>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14}/> Add FAQ</button>
      </div>

      <div style={{position:'relative'}}>
        <Search size={13} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--text3)'}}/>
        <input className="input" style={{paddingLeft:36}} placeholder="Search FAQs…" value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:'var(--space-sm)'}}>
        {filtered.map(faq=>(
          <div key={faq.id} className="card" style={{padding:0,cursor:'pointer'}} onClick={()=>setOpen(o=>o===faq.id?null:faq.id)}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'var(--space-md) var(--space-lg)',gap:'var(--space-md)'}}>
              <div style={{display:'flex',alignItems:'center',gap:'var(--space-sm)',minWidth:0}}>
                <span className="badge badge-gold" style={{flexShrink:0}}>{faq.cat}</span>
                <span style={{fontSize:'var(--fs-md)',fontWeight:500,color:'var(--text1)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{faq.q}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <button className="btn btn-ghost btn-icon" onClick={(e)=>{e.stopPropagation();openEdit(faq)}} aria-label="Edit FAQ"><Pencil size={14}/></button>
                <button className="btn btn-ghost btn-icon" onClick={(e)=>{e.stopPropagation();setActive(faq);setDeleteOpen(true)}} aria-label="Delete FAQ"><Trash2 size={14}/></button>
                {open===faq.id ? <ChevronDown size={16} style={{color:'var(--accent-hover)',flexShrink:0}}/> : <ChevronRight size={16} style={{color:'var(--text3)',flexShrink:0}}/>}
              </div>
            </div>
            {open===faq.id && (
              <div style={{padding:'var(--space-sm)',fontSize:'var(--fs-base)',color:'var(--text2)',lineHeight:1.7,borderTop:'1px solid var(--border-subtle)'}}>
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </div>
      <Modal
        open={modalOpen}
        title={active ? 'Edit FAQ' : 'Add FAQ'}
        subtitle="Maintain help content for admin users"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>{active ? 'Update FAQ' : 'Create FAQ'}</button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-field">
            <label>Question</label>
            <input className="input" placeholder="Type FAQ question" value={form.q} onChange={(e)=>setForm({...form, q:e.target.value})} />
          </div>
          <div className="form-field">
            <label>Answer</label>
            <textarea className="input" rows={4} placeholder="Type answer details" value={form.a} onChange={(e)=>setForm({...form, a:e.target.value})} style={{resize:'vertical'}} />
          </div>
          <div className="form-field">
            <label>Category</label>
            <select className="input" value={form.cat} onChange={(e)=>setForm({...form, cat:e.target.value})}><option>Account</option><option>Billing</option><option>Technical</option><option>Data</option></select>
          </div>
        </div>
      </Modal>
      <ConfirmModal
        open={deleteOpen}
        title="Delete FAQ"
        message={`Delete "${active?.q || 'selected FAQ'}"?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />
      <style>{`.dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}`}</style>
    </div>
  )
}
